import { WarningOutlined, GlobalOutlined } from '@ant-design/icons';
import { Space, Tag, Tooltip, Popover } from 'antd';
import React, { useEffect, useState } from 'react';
import { listDictionaries } from '@/services/admin/dictionary';

export interface DictionaryTagsProps {
  /** 字典类型，如 'job_position', 'job_location' 等 */
  type: string;
  /** 字典项的 code 值，可以是单个字符串或字符串数组（code 实际上是 id） */
  codes: string | string[] | null | undefined;
  /** 是否只显示启用的字典项（默认 true） */
  isActive?: boolean;
  /** 空值时的显示文本（默认 '-'） */
  emptyText?: string;
  /** Tag 之间的间距 */
  spaceSize?: number | [number, number];
  /** 是否换行显示 */
  wrap?: boolean;
}

/**
 * 字典项信息
 */
interface DictionaryItemInfo {
  /** value 字段的值 */
  value: string;
  /** display_text 多语言内容 */
  displayText?: Record<string, any>;
}

/**
 * 字典缓存
 * key: `${type}_${isActive}` -> Map<code, DictionaryItemInfo>
 */
const dictionaryCache = new Map<string, Map<string, DictionaryItemInfo>>();

/**
 * 正在加载的 Promise 缓存，避免并发请求
 * key: `${type}_${isActive}` -> Promise<Map<code, DictionaryItemInfo>>
 */
const loadingPromises = new Map<string, Promise<Map<string, DictionaryItemInfo>>>();

/**
 * 加载字典项列表（带缓存）
 */
const loadDictionaryItemsWithCache = async (
  type: string,
  isActive: boolean,
): Promise<Map<string, DictionaryItemInfo>> => {
  const cacheKey = `${type}_${isActive}`;

  // 如果缓存中有数据，直接返回
  const cached = dictionaryCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  // 如果正在加载，返回已有的 Promise
  const loadingPromise = loadingPromises.get(cacheKey);
  if (loadingPromise) {
    return loadingPromise;
  }

  // 创建新的加载 Promise
  const loadPromise = (async () => {
    try {
      // 使用列表接口获取完整的字典项信息
      const response = await listDictionaries({
        current: 1,
        pageSize: 1000, // 获取足够多的数据
        type,
        is_active: isActive !== false ? true : undefined,
      });
      const map = new Map<string, DictionaryItemInfo>();
      if (response.code === 0 && response.data?.list) {
        response.data.list.forEach((item) => {
          // code 实际上是 id，用于匹配
          map.set(item.code, {
            value: item.value,
            displayText: item.display_text,
          });
        });
      }
      // 缓存结果
      dictionaryCache.set(cacheKey, map);
      return map;
    } catch (error: any) {
      console.error(`加载字典项失败 (type: ${type}):`, error);
      // 即使失败也缓存一个空 Map，避免重复请求
      const emptyMap = new Map<string, DictionaryItemInfo>();
      dictionaryCache.set(cacheKey, emptyMap);
      return emptyMap;
    } finally {
      // 加载完成后清除 Promise 缓存
      loadingPromises.delete(cacheKey);
    }
  })();

  // 缓存 Promise
  loadingPromises.set(cacheKey, loadPromise);
  return loadPromise;
};

/**
 * 格式化多语言显示文本用于展示
 */
const formatDisplayText = (displayText?: Record<string, any>): Array<{ lang: string; label: string; text: string }> => {
  if (!displayText || typeof displayText !== 'object') {
    return [];
  }
  const items: Array<{ lang: string; label: string; text: string }> = [];
  
  const langMap: Record<string, string> = {
    zh_CN: '中文',
    en_US: 'English',
    km_KH: 'ភាសាខ្មែរ',
  };
  
  // 优先显示常用语言
  ['zh_CN', 'en_US', 'km_KH'].forEach((lang) => {
    if (displayText[lang]) {
      items.push({
        lang,
        label: langMap[lang] || lang,
        text: displayText[lang],
      });
    }
  });
  
  // 处理其他语言
  Object.entries(displayText).forEach(([lang, text]) => {
    if (!['zh_CN', 'en_US', 'km_KH'].includes(lang) && text) {
      items.push({
        lang,
        label: lang,
        text: String(text),
      });
    }
  });
  
  return items;
};

/**
 * 多语言展示内容组件
 */
const DisplayTextContent: React.FC<{ displayText: Record<string, any> }> = ({ displayText }) => {
  const items = formatDisplayText(displayText);
  
  if (items.length === 0) {
    return (
      <div style={{ padding: '4px 0', color: '#999', fontSize: 13 }}>
        暂无多语言信息
      </div>
    );
  }
  
  return (
    <div style={{ minWidth: 220, maxWidth: 320 }}>
      <div
        style={{
          marginBottom: 12,
          paddingBottom: 8,
          borderBottom: '1px solid #f0f0f0',
          fontSize: 13,
          color: '#666',
          fontWeight: 600,
        }}
      >
        多语言显示文本
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {items.map((item, index) => (
          <div
            key={item.lang}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              paddingBottom: index < items.length - 1 ? 8 : 0,
              borderBottom: index < items.length - 1 ? '1px solid #f5f5f5' : 'none',
            }}
          >
            <span
              style={{
                display: 'inline-block',
                minWidth: 70,
                fontSize: 12,
                color: '#8c8c8c',
                fontWeight: 500,
                flexShrink: 0,
              }}
            >
              {item.label}:
            </span>
            <span
              style={{
                flex: 1,
                fontSize: 13,
                color: '#262626',
                wordBreak: 'break-word',
                lineHeight: '20px',
              }}
            >
              {item.text}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * 字典项标签组件
 * 
 * 用于显示字典项的标签，支持单个或多个 code 值。
 * - 回显匹配基于 code（code 实际上是 id）
 * - 实际展示 value 字段的内容
 * - hover 时显示多语言的 display_text 内容
 * 
 * @example
 * ```tsx
 * // 单个 code
 * <DictionaryTags type="job_position" codes="dictionary_id" />
 * 
 * // 多个 codes
 * <DictionaryTags type="job_position" codes={["id1", "id2"]} />
 * 
 * // 在表格列中使用
 * {
 *   title: '所属行业',
 *   dataIndex: 'industry_codes',
 *   render: (_, record) => (
 *     <DictionaryTags type="industry" codes={record.industry_codes} />
 *   ),
 * }
 * ```
 */
const DictionaryTags: React.FC<DictionaryTagsProps> = ({
  type,
  codes,
  isActive = true,
  emptyText = '-',
  spaceSize = [0, 8],
  wrap = true,
}) => {
  // 字典项映射：code -> DictionaryItemInfo
  const [dictionaryMap, setDictionaryMap] = useState<Map<string, DictionaryItemInfo>>(new Map());
  const [loading, setLoading] = useState(true);

  // 加载字典项（使用缓存）
  useEffect(() => {
    if (!type) {
      setLoading(false);
      return;
    }

    setLoading(true);
    loadDictionaryItemsWithCache(type, isActive)
      .then((map) => {
        setDictionaryMap(map);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [type, isActive]);

  // 如果没有 codes，显示空值文本
  if (!codes || (Array.isArray(codes) && codes.length === 0)) {
    return <span>{emptyText}</span>;
  }

  // 统一转换为数组处理
  const codesArray = Array.isArray(codes) ? codes : [codes];

  // 如果正在加载，显示占位符
  if (loading) {
    return (
      <Space size={spaceSize} wrap={wrap}>
        {codesArray.map((code) => (
          <Tag key={code}>加载中...</Tag>
        ))}
      </Space>
    );
  }

  return (
    <Space size={spaceSize} wrap={wrap}>
      {codesArray.map((code) => {
        const itemInfo = dictionaryMap.get(code);
        const isFallback = !itemInfo;
        const displayValue = itemInfo?.value || code;
        const displayText = itemInfo?.displayText;
        const hasDisplayText = displayText && Object.keys(displayText).length > 0;

        // 字典项标签样式 - 明显区别于普通标签
        const tagStyle: React.CSSProperties = {
          margin: 0,
          padding: '2px 10px',
          borderRadius: '4px',
          border: '1px solid #d9d9d9',
          backgroundColor: '#f5f5f5',
          color: '#595959',
          fontSize: '13px',
          lineHeight: '22px',
          cursor: hasDisplayText ? 'help' : 'default',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          transition: 'all 0.2s ease',
        };

        const tagContent = (
          <Tag
            key={code}
            style={tagStyle}
            color={isFallback ? 'warning' : undefined}
            className={hasDisplayText ? 'dictionary-tag-with-i18n' : undefined}
            onMouseEnter={(e) => {
              if (hasDisplayText && !isFallback) {
                const target = e.currentTarget;
                target.style.borderColor = '#1890ff';
                target.style.backgroundColor = '#e6f7ff';
                target.style.color = '#1890ff';
              }
            }}
            onMouseLeave={(e) => {
              if (!isFallback) {
                const target = e.currentTarget;
                target.style.borderColor = '#d9d9d9';
                target.style.backgroundColor = '#f5f5f5';
                target.style.color = '#595959';
              }
            }}
          >
            {hasDisplayText && !isFallback && (
              <GlobalOutlined style={{ fontSize: 12, color: '#1890ff' }} />
            )}
            {isFallback && <WarningOutlined style={{ marginRight: 4, fontSize: 12 }} />}
            <span>{displayValue}</span>
          </Tag>
        );

        // 如果有多语言信息，使用 Popover 展示
        if (hasDisplayText && !isFallback) {
          return (
            <Popover
              key={code}
              content={<DisplayTextContent displayText={displayText} />}
              trigger="hover"
              placement="top"
              overlayStyle={{ maxWidth: 400 }}
              overlayInnerStyle={{ padding: '16px' }}
              mouseEnterDelay={0.3}
              mouseLeaveDelay={0.1}
            >
              {tagContent}
            </Popover>
          );
        }

        // 如果是 fallback 或没有多语言信息，使用 Tooltip
        return (
          <Tooltip
            key={code}
            title={isFallback ? `字典项不存在，显示原始 code: ${code}` : undefined}
          >
            {tagContent}
          </Tooltip>
        );
      })}
    </Space>
  );
};

export default DictionaryTags;
